import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { getMyNickname, updateMyNickname } from '../api/community';
import useBackButtonClose from '../utils/useBackButtonClose';

// Modal for viewing/changing user's nickname.
// `open` controls visibility; `onClose` invoked on close (with optional new nickname).
// `onSave(newNickname)` called after successful save.

export default function NicknameModal({ open, onClose, onSave }) {
  const { user, getIdToken } = useAuth();
  const [current, setCurrent] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !user) return;
    let active = true;
    setLoading(true); setError('');
    (async () => {
      try {
        const token = await getIdToken();
        if (!token) throw new Error('세션이 만료되었습니다');
        const data = await getMyNickname(token);
        if (!active) return;
        setCurrent(data.nickname);
        setIsCustom(data.isCustom);
        setInput(data.nickname);
      } catch (err) {
        if (active) setError(err.message);
      }
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [open, user, getIdToken]);

  // 모바일 뒤로가기로 모달만 닫기(SPA 이탈 방지). 닫기 = onClose(null).
  useBackButtonClose(open && !!user, () => onClose(null));

  if (!open) return null;
  if (!user) {
    return null;
  }

  const trimmed = input.trim();
  const canSave = trimmed.length >= 2 && trimmed.length <= 20 && trimmed !== current;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true); setError('');
    try {
      const token = await getIdToken();
      if (!token) throw new Error('세션이 만료되었습니다');
      const data = await updateMyNickname({ token, nickname: trimmed });
      if (onSave) onSave(data.nickname);
      onClose(data.nickname);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  // Portal to document.body — escapes NavBar's containing block
  // (backdrop-filter on scrolled nav otherwise pins position:fixed children
  // to the 64px nav strip, cutting the modal off above the viewport).
  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      // zIndex 200 < NavBar 1000; paddingTop 84 = nav 64 + gutter 20
      zIndex: 200, padding: '84px 20px 20px',
    }} onClick={() => onClose(null)}>
      <form onSubmit={submit} className="card"
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface)', padding: 28,
          borderRadius: 'var(--radius)', maxWidth: 400, width: '100%',
        }}>
        <h2 style={{ margin: '0 0 12px 0', fontSize: '1.2rem' }}>닉네임 설정</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 16 }}>
          {isCustom
            ? '커뮤니티에 표시되는 닉네임입니다. 24시간에 한 번 변경 가능합니다.'
            : '커뮤니티에 자동 부여된 닉네임입니다. 원하시면 변경하세요.'}
        </p>
        {loading ? (
          <p style={{ color: 'var(--text-muted)' }}>로딩 중...</p>
        ) : (
          <>
            <input
              className="input"
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="2~20자, 한글/영문/숫자/_"
              maxLength={20}
              autoFocus
              style={{ width: '100%', marginBottom: 12 }}
            />
            {error && (
              <p style={{ color: '#ff5252', fontSize: '0.85rem', marginBottom: 12 }}>❌ {error}</p>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-outline" onClick={() => onClose(null)}
                style={{ padding: '8px 16px', fontSize: '0.9rem' }}>
                취소
              </button>
              <button type="submit" className="btn btn-primary"
                disabled={!canSave || saving}
                style={{ padding: '8px 18px', fontSize: '0.9rem' }}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </>
        )}
      </form>
    </div>,
    document.body
  );
}
